import BigNumber from 'bignumber.js'
import { Token } from 'config/types'
import Column from 'layout/Components/Column'
import { RowMiddle } from 'layout/Components/Row'
import { useCallback, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import { Trans, useTranslation } from 'react-i18next'
import { useTokenPriorities } from 'state/app/hooks'
import { BoxProps } from 'UIKit/Box/types'
import { ButtonProps } from 'UIKit/Button/types'
import Text from 'UIKit/Text'
import TokenLogo from 'UIKit/TokenLogo'
import { filterTokenAmountSearch } from 'utils/token'
import { PaymentPageEnums } from 'views/Payment/Menu'
import CurrencyRowItem from 'views/Payment/MobilePayment/components/PaymentTokenSelectModal/CurrencyRowItem'
import { useStoredSearchToken } from 'views/Payment/MobilePayment/components/PaymentTokenSelectModal/useStoredSearchToken'
import { CurrencyDisplayInfo } from 'views/Payment/MobilePayment/components/SelectedCurrencyItem'
import {
  EmptyCurrencyResponse,
  EmptyMessageEnum,
  PaymentAppSearch,
  PaymentFormControl,
  PaymentSuggestCurrencyHistory,
  TextSelect,
} from 'views/Payment/MobilePayment/components/styles'
import { extractPaymentTokenKey, useFetchOptionsCurrencyBalance } from 'views/Payment/MobilePayment/hooks/useTokens'
import { PaymentSelect, PaymentSelectTrigger, SelectContentInjectedProps, SelectScroll } from '../PaymentSelect'
import { useFilterOptionsBySearch } from '../PaymentSelect/hooks'

interface TokenSelectProps {
  options: Token[]
  allCurrencies: Token[]
  fetchTokenBalanceFn?: (token: Token) => Promise<BigNumber>
  onTokenChange: (token: Token) => void
  value: Token
  disabled?: boolean
  dropdownContentProps?: BoxProps
  hideHistory?: boolean
  isIncludeFiat?: boolean
  hideNetwork?: boolean
  from: PaymentPageEnums
  scrollHeight?: number | string
}

const CurrencyDropdownContent = ({
  options,
  onSelect,
  hideHistory,
  isIncludeFiat,
  fetchTokenBalanceFn,
  hideNetwork,
  allCurrencies,
  from,
  scrollHeight,
}: Partial<SelectContentInjectedProps<Token> & TokenSelectProps>) => {
  const { t } = useTranslation()
  const { handleSortTokens } = useTokenPriorities({
    from,
  })
  const sortOptions = useMemo(() => handleSortTokens(options), [options])
  const { currencies, fetchBalance: fetchBalanceControl } = useFetchOptionsCurrencyBalance({
    curriencies: sortOptions,
    fetchTokenBalanceFn,
    from,
  })

  const {
    onChange,
    options: formatOptions,
    typedValue,
  } = useFilterOptionsBySearch({
    options: currencies,
    filterFn: filterTokenAmountSearch,
  })
  const storedSearch = useStoredSearchToken(from)

  return (
    <Column gap="var(--pm-space-component)">
      <PaymentAppSearch autoFocus={!isMobile} placeholder={t('Search Coins')} onValueChanged={onChange} />

      <SelectScroll
        position="relative"
        gap="var(--pm-space-component)"
        maxHeight={scrollHeight}
        minHeight={scrollHeight || 'auto'}
      >
        {!hideHistory && !typedValue && (
          <PaymentSuggestCurrencyHistory
            hideNetwork={hideNetwork}
            isIncludeFiat={isIncludeFiat}
            control={storedSearch}
            onSelectKey={(str) => {
              const { tokenCode, network } = extractPaymentTokenKey(str)
              const searchByOptions = options.find((p) => p.code === tokenCode)
              const selectedToken =
                allCurrencies.find((p) => p.code === tokenCode && p.network === network) || searchByOptions

              if (selectedToken) {
                onSelect(selectedToken)
                storedSearch.record(selectedToken.code, selectedToken.address, selectedToken.network, hideNetwork)
              } else {
                storedSearch.remove(str)
              }
            }}
          />
        )}

        <Column>
          <Text
            style={{
              position: 'sticky',
              top: '0',
              zIndex: '1',
              background: 'var(--pm-dropdown-color)',
            }}
            small
            color="var(--pm-label-color)"
            bold
            pb="8px"
          >
            <Trans>List coins</Trans>
          </Text>

          <Column gap="var(--pm-space-label, 8px)">
            {formatOptions.length > 0
              ? formatOptions.map((currency) => (
                  <CurrencyRowItem
                    hideNetwork={hideNetwork}
                    key={`${currency.token.displayName}-${currency.token.network}`}
                    currency={currency.token}
                    amount={currency.amount}
                    fetchBalance={fetchBalanceControl}
                    from={from}
                    onSelect={() => {
                      storedSearch.record(
                        currency.token.code,
                        currency.token.address,
                        currency.token.network,
                        hideNetwork,
                      )
                      onSelect(currency.token)
                    }}
                  />
                ))
              : typedValue && <EmptyCurrencyResponse message={EmptyMessageEnum.Currency} />}
          </Column>
        </Column>
      </SelectScroll>
    </Column>
  )
}

const CurrencySelectDropdown: React.FC<
  TokenSelectProps & {
    triggerProps?: ButtonProps
  }
> = ({
  options,
  allCurrencies,
  onTokenChange,
  value,
  triggerProps = {},
  hideHistory = false,
  isIncludeFiat = false,
  fetchTokenBalanceFn,
  dropdownContentProps,
  hideNetwork,
  from,
  scrollHeight,
  disabled,
}) => {
  const { t } = useTranslation()
  const RenderContent = useCallback(
    (props) => {
      return (
        <CurrencyDropdownContent
          value={value}
          isIncludeFiat={isIncludeFiat}
          hideHistory={hideHistory}
          fetchTokenBalanceFn={fetchTokenBalanceFn}
          dropdownContentProps={dropdownContentProps}
          hideNetwork={hideNetwork}
          allCurrencies={allCurrencies}
          from={from}
          scrollHeight={scrollHeight}
          {...props}
        />
      )
    },
    [
      value,
      isIncludeFiat,
      hideHistory,
      fetchTokenBalanceFn,
      dropdownContentProps,
      hideNetwork,
      allCurrencies,
      from,
      scrollHeight,
    ],
  )

  return (
    <PaymentFormControl
      state={{
        errors: [],
        value: '',
        isDirty: false,
      }}
      formatErrorMessage={() => ''}
      label={t('Select Coin')}
      disabled={disabled}
    >
      <PaymentSelect<Token>
        disabled={disabled}
        options={options}
        onSelect={onTokenChange}
        DropdownContent={RenderContent}
      >
        <PaymentSelectTrigger {...triggerProps}>
          {value ? (
            <RowMiddle gap="var(--pm-space-label, 8px)" id="token-select-item">
              <TokenLogo token={value} hideNetwork={hideNetwork} size={20} width={20} height={20} />
              <CurrencyDisplayInfo token={value} hideNetwork={hideNetwork} />
            </RowMiddle>
          ) : (
            <TextSelect placeholder>
              <Trans>Select Token</Trans>
            </TextSelect>
          )}
        </PaymentSelectTrigger>
      </PaymentSelect>
    </PaymentFormControl>
  )
}

export default CurrencySelectDropdown
